<script setup lang="ts">
import { useI18n } from "vue-i18n";
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const { t } = useI18n();
</script>
<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main id="main-content" class="max-w-7xl mx-auto px-5 py-8">
      <div id="breadcrumb" class="mb-6">
        <nav class="flex items-center space-x-2 text-sm text-gray-500">
          <span class="hover:text-primary cursor-pointer">{{ $t('developer.project.submit_pdd.breadcrumb.dashboard') }}</span>
          <i class="fas fa-chevron-right text-xs"></i>
          <span class="hover:text-primary cursor-pointer">{{ $t('developer.project.submit_pdd.breadcrumb.projects') }}</span>
          <i class="fas fa-chevron-right text-xs"></i>
          <span class="text-gray-900">{{ $t('developer.project.submit_pdd.breadcrumb.submit_pdd') }}</span>
        </nav>
      </div>

      <div id="pdd-header" class="mb-8">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-accent mb-2">
              {{ $t('developer.project.submit_pdd.title') }}
            </h1>
            <p class="text-gray-600">
              {{ $t('developer.project.submit_pdd.subtitle') }}
            </p>
          </div>
          <div class="flex items-center space-x-3">
            <span
              class="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium"
            >
              <i class="fas fa-clock mr-1"></i>
              {{ $t('developer.project.submit_pdd.status_draft') }}
            </span>
            <button class="text-gray-500 hover:text-primary">
              <i class="fas fa-question-circle text-lg"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div id="pdd-form" class="lg:col-span-3">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div id="project-info-section" class="mb-8">
              <h2
                class="text-xl font-semibold text-accent mb-4 flex items-center"
              >
                <i class="fas fa-info-circle text-primary mr-2"></i>
                {{ $t('developer.project.submit_pdd.project_info.title') }}
              </h2>
              <div
                class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6"
              >
                <div class="flex items-center space-x-2 mb-2">
                  <i class="fas fa-check-circle text-green-600"></i>
                  <span class="text-sm font-medium text-green-800"
                    >{{ $t('developer.project.submit_pdd.project_info.auto_filled') }}</span
                  >
                </div>
                <p class="text-sm text-green-700">
                  {{ $t('developer.project.submit_pdd.project_info.auto_filled_description') }}
                </p>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.project.submit_pdd.project_info.project_title') }}</label
                  >
                  <input
                    type="text"
                    value="Reforestation Initiative - Amazon Basin"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                    readonly
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.project.submit_pdd.project_info.project_category') }}</label
                  >
                  <input
                    type="text"
                    value="Category A - Large Scale"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                    readonly
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.project.submit_pdd.project_info.location') }}</label
                  >
                  <input
                    type="text"
                    value="Brazil, Para State"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                    readonly
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.project.submit_pdd.project_info.estimated_credits') }}</label
                  >
                  <input
                    type="text"
                    value="2,500,000"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                    readonly
                  />
                </div>
              </div>
            </div>

            <div id="doe-selection" class="mb-8">
              <h2
                class="text-xl font-semibold text-accent mb-4 flex items-center"
              >
                <i class="fas fa-clipboard-check text-primary mr-2"></i>
                {{ $t('developer.project.submit_pdd.doe_selection.title') }}
              </h2>
              <div
                class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
              >
                <div class="flex items-start space-x-3">
                  <i class="fas fa-info-circle text-blue-600 mt-1"></i>
                  <div>
                    <p class="text-sm font-medium text-blue-800 mb-1">
                      {{ $t('developer.project.submit_pdd.doe_selection.required_message') }}
                    </p>
                    <p class="text-sm text-blue-700">
                      {{ $t('developer.project.submit_pdd.doe_selection.description') }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.project.submit_pdd.doe_selection.project_sector') }}</label
                  >
                  <input
                    type="text"
                    value="Forestry and Land Use"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                    readonly
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('developer.project.submit_pdd.doe_selection.select_doe') }} *</label
                  >
                  <select
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">{{ $t('developer.project.submit_pdd.doe_selection.choose_doe_placeholder') }}</option>
                    <option value="doe1">
                      {{ $t('developer.project.submit_pdd.doe_selection.doe_options.ecoverify') }}
                    </option>
                    <option value="doe2">
                      {{ $t('developer.project.submit_pdd.doe_selection.doe_options.carbon_audit') }}
                    </option>
                    <option value="doe3">
                      {{ $t('developer.project.submit_pdd.doe_selection.doe_options.green_validation') }}
                    </option>
                    <option value="doe4">
                      {{ $t('developer.project.submit_pdd.doe_selection.doe_options.forest_carbon') }}
                    </option>
                  </select>
                </div>
              </div>

              <div id="conflict-check" class="mt-4 p-4 bg-gray-50 rounded-lg">
                <div class="flex items-center space-x-2 mb-2">
                  <i class="fas fa-shield-alt text-gray-600"></i>
                  <span class="text-sm font-medium text-gray-700"
                    >{{ $t('developer.project.submit_pdd.doe_selection.conflict_check.title') }}</span
                  >
                </div>
                <p class="text-sm text-gray-600">
                  {{ $t('developer.project.submit_pdd.doe_selection.conflict_check.description') }}
                </p>
              </div>
            </div>

            <div id="documents-section" class="mb-8">
              <h2
                class="text-xl font-semibold text-accent mb-4 flex items-center"
              >
                <i class="fas fa-file-upload text-primary mr-2"></i>
                {{ $t('developer.project.submit_pdd.documents.title') }}
              </h2>

              <div class="space-y-6">
                <div class="border border-gray-200 rounded-lg p-4">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center space-x-3">
                      <i class="fas fa-file-check text-green-600"></i>
                      <div>
                        <h3 class="font-medium text-gray-900">
                          {{ $t('developer.project.submit_pdd.documents.positive_validation_report.title') }}
                        </h3>
                        <p class="text-sm text-gray-600">{{ $t('developer.project.submit_pdd.documents.positive_validation_report.description') }}</p>
                      </div>
                    </div>
                    <span class="text-sm text-red-600 font-medium"
                      >{{ $t('developer.project.submit_pdd.documents.required_status') }}</span
                    >
                  </div>
                  <div
                    class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center"
                  >
                    <i
                      class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                    ></i>
                    <p class="text-sm text-gray-600 mb-2">
                      {{ $t('developer.project.submit_pdd.documents.upload_text') }}
                    </p>
                    <button
                      class="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      {{ $t('developer.project.submit_pdd.documents.choose_file') }}
                    </button>
                  </div>
                </div>

                <div class="border border-gray-200 rounded-lg p-4">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center space-x-3">
                      <i class="fas fa-file-alt text-blue-600"></i>
                      <div>
                        <h3 class="font-medium text-gray-900">
                          {{ $t('developer.project.submit_pdd.documents.updated_npramp.title') }}
                        </h3>
                        <p class="text-sm text-gray-600">
                          {{ $t('developer.project.submit_pdd.documents.updated_npramp.description') }}
                        </p>
                      </div>
                    </div>
                    <span class="text-sm text-green-600 font-medium"
                      >{{ $t('developer.project.submit_pdd.documents.auto_filled_status') }}</span
                    >
                  </div>
                  <div
                    class="bg-green-50 border border-green-200 rounded-lg p-3"
                  >
                    <div class="flex items-center space-x-2">
                      <i class="fas fa-check-circle text-green-600"></i>
                      <span class="text-sm text-green-800"
                        >npramp_amazon_reforestation_v2.pdf</span
                      >
                      <button
                        class="text-green-600 hover:text-green-800 ml-auto"
                      >
                        <i class="fas fa-download"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="border border-gray-200 rounded-lg p-4">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center space-x-3">
                      <i class="fas fa-file-alt text-blue-600"></i>
                      <div>
                        <h3 class="font-medium text-gray-900">
                          {{ $t('developer.project.submit_pdd.documents.updated_ehmf.title') }}
                        </h3>
                        <p class="text-sm text-gray-600">
                          {{ $t('developer.project.submit_pdd.documents.updated_ehmf.description') }}
                        </p>
                      </div>
                    </div>
                    <span class="text-sm text-green-600 font-medium"
                      >{{ $t('developer.project.submit_pdd.documents.auto_filled_status') }}</span
                    >
                  </div>
                  <div
                    class="bg-green-50 border border-green-200 rounded-lg p-3"
                  >
                    <div class="flex items-center space-x-2">
                      <i class="fas fa-check-circle text-green-600"></i>
                      <span class="text-sm text-green-800"
                        >ehmf_environmental_assessment_v2.pdf</span
                      >
                      <button
                        class="text-green-600 hover:text-green-800 ml-auto"
                      >
                        <i class="fas fa-download"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="border border-gray-200 rounded-lg p-4">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center space-x-3">
                      <i class="fas fa-users text-purple-600"></i>
                      <div>
                        <h3 class="font-medium text-gray-900">
                          {{ $t('developer.project.submit_pdd.documents.long_term_financial_proposal.title') }}
                        </h3>
                        <p class="text-sm text-gray-600">
                          {{ $t('developer.project.submit_pdd.documents.long_term_financial_proposal.description') }}
                        </p>
                      </div>
                    </div>
                    <span class="text-sm text-red-600 font-medium"
                      >{{ $t('developer.project.submit_pdd.documents.required_status') }}</span
                    >
                  </div>
                  <div
                    class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center"
                  >
                    <i
                      class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                    ></i>
                    <p class="text-sm text-gray-600 mb-2">
                      {{ $t('developer.project.submit_pdd.documents.upload_text') }}
                    </p>
                    <button
                      class="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      {{ $t('developer.project.submit_pdd.documents.choose_file') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              id="submission-actions"
              class="flex items-center justify-between pt-6 border-t border-gray-200"
            >
              <div class="flex items-center space-x-4">
                <button
                  class="text-gray-600 hover:text-primary flex items-center space-x-2"
                >
                  <i class="fas fa-save"></i>
                  <span>{{ $t('developer.project.submit_pdd.actions.save_draft') }}</span>
                </button>
                <button
                  class="text-gray-600 hover:text-primary flex items-center space-x-2"
                >
                  <i class="fas fa-eye"></i>
                  <span>{{ $t('developer.project.submit_pdd.actions.preview') }}</span>
                </button>
              </div>
              <div class="flex items-center space-x-3">
                <button
                  class="border border-gray-300 text-gray-700 px-6 py-2 rounded-md font-medium hover:bg-gray-50"
                >
                  {{ $t('developer.project.submit_pdd.actions.cancel') }}
                </button>
                <button
                  class="bg-primary text-white px-6 py-2 rounded-md font-medium hover:bg-primary/90"
                >
                  {{ $t('developer.project.submit_pdd.actions.submit_pdd') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div id="sidebar" class="lg:col-span-1">
          <div class="space-y-6">
            <div
              id="progress-card"
              class="bg-white rounded-lg border border-gray-200 p-6"
            >
              <h3 class="font-semibold text-accent mb-4">
                {{ $t('developer.project.submit_pdd.sidebar.progress_card.title') }}
              </h3>
              <div class="space-y-4">
                <div class="flex items-center space-x-3">
                  <div
                    class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"
                  >
                    <i class="fas fa-check text-green-600 text-sm"></i>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-900">
                      {{ $t('developer.project.submit_pdd.sidebar.progress_card.pin_approved.title') }}
                    </p>
                    <p class="text-xs text-gray-500">Dec 1, 2024</p>
                  </div>
                </div>
                <div class="flex items-center space-x-3">
                  <div
                    class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center"
                  >
                    <i class="fas fa-circle text-blue-600 text-xs"></i>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-900">
                      {{ $t('developer.project.submit_pdd.sidebar.progress_card.pdd_submission.title') }}
                    </p>
                    <p class="text-xs text-gray-500">{{ $t('developer.project.submit_pdd.sidebar.progress_card.pdd_submission.status') }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-3">
                  <div
                    class="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                  >
                    <i class="fas fa-circle text-gray-400 text-xs"></i>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-500">
                      {{ $t('developer.project.submit_pdd.sidebar.progress_card.doe_validation.title') }}
                    </p>
                    <p class="text-xs text-gray-400">{{ $t('developer.project.submit_pdd.sidebar.progress_card.doe_validation.status') }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div
              id="timeline-card"
              class="bg-white rounded-lg border border-gray-200 p-6"
            >
              <h3 class="font-semibold text-accent mb-4">
                {{ $t('developer.project.submit_pdd.sidebar.timeline_card.title') }}
              </h3>
              <div class="space-y-3">
                <div class="flex items-center space-x-3">
                  <i class="fas fa-clock text-orange-500"></i>
                  <div>
                    <p class="text-sm font-medium text-gray-900">
                      {{ $t('developer.project.submit_pdd.sidebar.timeline_card.pdd_deadline.title') }}
                    </p>
                    <p class="text-xs text-gray-500">{{ $t('developer.project.submit_pdd.sidebar.timeline_card.pdd_deadline.description') }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-3">
                  <i class="fas fa-calendar text-blue-500"></i>
                  <div>
                    <p class="text-sm font-medium text-gray-900">
                      {{ $t('developer.project.submit_pdd.sidebar.timeline_card.doe_selection.title') }}
                    </p>
                    <p class="text-xs text-gray-500">{{ $t('developer.project.submit_pdd.sidebar.timeline_card.doe_selection.description') }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div
              id="help-card"
              class="bg-blue-50 rounded-lg border border-blue-200 p-6"
            >
              <h3 class="font-semibold text-blue-900 mb-2">{{ $t('developer.project.submit_pdd.sidebar.help_card.title') }}</h3>
              <p class="text-sm text-blue-800 mb-4">
                {{ $t('developer.project.submit_pdd.sidebar.help_card.description') }}
              </p>
              <div class="space-y-2">
                <button
                  class="w-full bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700"
                >
                  <i class="fas fa-headset mr-2"></i>
                  {{ $t('developer.project.submit_pdd.sidebar.help_card.contact_support') }}
                </button>
                <button
                  class="w-full border border-blue-600 text-blue-600 py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-50"
                >
                  <i class="fas fa-book mr-2"></i>
                  {{ $t('developer.project.submit_pdd.sidebar.help_card.view_guide') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
